import { isNil } from 'lodash';
import { useCallback, useMemo } from 'react';

import { CommPlatform } from '@src/api/generated/data-contracts';
import useNav from '@src/hooks/useNav';

import { BASE_LANGUAGE, normalizeLanguage } from '../constants/language';

export type QueryParams = {
  platform: CommPlatform;
  pid: string;
  /**
   * 内容语言。放在 URL 里而不是只存 localStorage，是为了让链接自带语言：
   * 搜索结果、分享出去的链接、跨页跳转都能落到正确语言，而不依赖对端本地状态。
   */
  language: string;
};

// 将查询参数转换为URLSearchParams
function toSearchParams(params: QueryParams): URLSearchParams {
  const searchParams = new URLSearchParams();

  Object.keys(params).forEach((key) => {
    const item = (params as any)[key];
    if (isNil(item) || item === '') return;
    // 语言始终显式写进 URL（含 en）：地址栏 / 分享出去的链接自带语言，
    // 不受接收方本地偏好影响（"缺省=英文"只用于改造前的老链接兜底）
    searchParams.set(key, String(item));
  });

  return searchParams;
}

// 将 url query string 转换为查询参数
function parseQueryParams(queryStr: string, defaultData: QueryParams): QueryParams {
  const result: QueryParams = { ...defaultData };
  const searchParams = new URLSearchParams(queryStr);
  searchParams.forEach((value, key) => {
    if (value === '' || isNil(value)) return;
    if (key === 'platform' && value !== 'publisher' && value !== 'advertiser' && value !== 'mediation') return;
    (result as any)[key] = value;
  });
  // 未启用的语言收敛到基准语言：老链接、下线某语言后的旧分享链接都不能白屏
  result.language = normalizeLanguage(result.language);
  return result;
}

function useSearch() {
  const nav = useNav();
  const { pathname, search } = nav.location;

  // 参数只以地址栏为唯一来源：原先 useState 读一次，前进/后退只改 URL 不重跑初始化 → pid 变了页面不变
  const params = useMemo<QueryParams>(() => parseQueryParams(search, {
    platform: 'publisher',
    pid: '',
    // 语言只以 URL 为准（没带就是英文），不读本地偏好：否则同一链接在不同机器/标签页打开的
    // 语言可能不同；要表达中文就显式带 language=zh（内页链接由 buildLanguageQuery 拼好）
    language: BASE_LANGUAGE,
  }), [search]);

  const updateParams = useCallback((newParams: Partial<QueryParams>) => {
    const finalParams = { ...params, ...newParams };
    const nextSearch = toSearchParams(finalParams);
    nav.to(pathname, { search: Object.fromEntries(nextSearch) });
  }, [nav, params, pathname]);

  return { params, updateParams };
}

export default useSearch;
